import { Users } from "lucide-react";
import type { FC, ReactNode } from "react";
import React from "react";
import { TableVirtuoso } from "react-virtuoso";

import type { Identity, User } from "@core/domains/identity";

import { useInfiniteQuery } from "@react/foundation/query";
import { Skeleton } from "@react/ui-primitives/skeleton";
import {
  Table,
  TableBody,
  TableCard,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@react/ui-primitives/table";

import { PageView } from "../../../shared/components/PageView.tsx";
import { useQueries } from "../../../shared/hooks/useQueries.ts";
import { identityLabel } from "../../../shared/lib/identity.ts";

const COLUMNS = 2;

interface AccountsViewProps {
  readonly renderUserActions: (
    user: User,
    identities: readonly Identity[],
  ) => ReactNode;
}

export const AccountsView: FC<AccountsViewProps> = ({ renderUserActions }) => {
  const { getUsersQueryOptions } = useQueries();
  const usersQueryOptions = getUsersQueryOptions({});
  const users = useInfiniteQuery(usersQueryOptions.infinite);

  return (
    <div className="mx-auto w-full">
      <PageView
        icon={<Users />}
        subtitle={`${users.items.length} user(s)`}
        title="Accounts"
      >
        <TableCard>
          {users.status === "pending" && (
            <Table className="table-fixed">
              <TableHeader>
                <TableRow variant="header">
                  <TableHead className="w-[60%]">User</TableHead>
                  <TableHead className="w-[40%] text-right">Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.from({ length: 3 }, (_1, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: COLUMNS }, (_2, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text-line" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {users.status === "error" && (
            <Table className="table-fixed">
              <TableHeader>
                <TableRow variant="header">
                  <TableHead className="w-[60%]">User</TableHead>
                  <TableHead className="w-[40%] text-right">Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell colSpan={COLUMNS} variant="error">
                    Failed to load users.
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
          {users.status === "success" && users.items.length === 0 && (
            <Table className="table-fixed">
              <TableHeader>
                <TableRow variant="header">
                  <TableHead className="w-[60%]">User</TableHead>
                  <TableHead className="w-[40%] text-right">Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell colSpan={COLUMNS} variant="info">
                    No users found.
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
          {users.status === "success" && users.items.length > 0 && (
            <TableVirtuoso
              components={{
                Table: props => <Table className="table-fixed" {...props} />,
                TableBody,
                TableHead: TableHeader,
                TableRow,
              }}
              computeItemKey={(_index, row) => row.user.id}
              data={users.items}
              endReached={users.loadNextPage}
              fixedHeaderContent={() => (
                <TableRow variant="header">
                  <TableHead className="w-[60%]">User</TableHead>
                  <TableHead className="w-[40%] text-right">Balance</TableHead>
                </TableRow>
              )}
              itemContent={(_index, row) => (
                <>
                  <TableCell>{identityLabel(row.identities)}</TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end">
                      {renderUserActions(row.user, row.identities)}
                    </div>
                  </TableCell>
                </>
              )}
              useWindowScroll
            />
          )}
        </TableCard>
      </PageView>
    </div>
  );
};
